import React, { useState, useEffect } from "react";
import logo from "../../assets/images/logo-rewind.png";
import { useTranslation } from "react-i18next";
import { getProjects } from "../../api";
import { DropdownContent } from "../../types/types";
import Dropdown from "../common/Dropdown";
import TextInput from "../ui/TextInput";
import Button from "../ui/Button";
import studio from "../../components/modals/StudioModal.module.css";
import { ModalPortal } from "./ModalPortal";

interface VariantOption {
    id: string;
    title: string;
    isInProject: boolean;
    projectName?: string;
}

interface SelectVariantsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddToProject: (projectId: string, newProjectName: string, selectedVariantIds: string[]) => void;
    variants: VariantOption[];
    placeholder?: string;
}

const SelectVariantsModal: React.FC<SelectVariantsModalProps> = ({
    isOpen,
    onClose,
    onAddToProject,
    variants,
    placeholder,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [newProjectName, setNewProjectName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const token = localStorage.getItem("token");
    const [projects, setProjects] = useState<DropdownContent[]>([]);
    const [selectedVariants, setSelectedVariants] = useState<Set<string>>(new Set());

    // Initialize selected variants: all variants that are NOT in a project
    useEffect(() => {
        if (isOpen && variants.length > 0) {
            const notInProject = variants.filter((v) => !v.isInProject).map((v) => v.id);
            setSelectedVariants(new Set(notInProject));
        }
    }, [isOpen, variants]);

    const projectOptions: DropdownContent[] = [{ value: "new", label: t("createNewProject") }, ...projects];

    const handleAddToProject = () => {
        if (selectedProject || newProjectName) {
            if (selectedVariants.size === 0) {
                alert(t("pleaseSelectAtLeastOneVariant") || "Please select at least one variant");
                return;
            }
            onAddToProject(selectedProject, newProjectName, Array.from(selectedVariants));
            onClose();
        } else {
            alert(t("warningSelectProject"));
        }
    };

    const toggleVariant = (variantId: string) => {
        const variant = variants.find((v) => v.id === variantId);
        if (variant?.isInProject) {
            return;
        }
        setSelectedVariants((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(variantId)) {
                newSet.delete(variantId);
            } else {
                newSet.add(variantId);
            }
            return newSet;
        });
    };

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                if (!token) {
                    setError(t("noToken"));
                    return;
                }
                const response = await getProjects({ token });
                if (!response) {
                    setError(t("failedToFetchProjects"));
                } else {
                    const projectsList: DropdownContent[] = response.map((project: { id: string; name: string }) => ({
                        label: project.name,
                        value: project.id,
                    }));
                    setProjects(projectsList);
                }
            } catch (err) {
                setError(t("errorFetchingProjects"));
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchProjects();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, isOpen]);

    if (!isOpen) return null;

    const isProjectSelected = selectedProject === "new" ? newProjectName.trim() !== "" : selectedProject !== "";
    const modalTitle = placeholder && placeholder !== "" ? placeholder : t("addThisTextToAProject");

    return (
        <ModalPortal>
            <div className={studio.overlayTop}>
                <div className={`${studio.projectModalContent} ${studio.variantsModal}`}>
                    <div className={studio.logoWrap}>
                        <img src={logo} alt="Logo" />
                    </div>
                    <h1 className={studio.projectModalTitle}>{modalTitle}</h1>

                    <h2 className={`${studio.variantsStepTitle} ${studio.variantsStepTitleFirst}`}>
                        {t("step1SelectAudios") || "Step 1: Select the audios"}
                    </h2>

                    <div className={studio.projectSection}>
                        <div className={studio.variantsList}>
                            {variants.map((variant) => {
                                const isSelected = selectedVariants.has(variant.id);
                                const isDisabled = variant.isInProject;
                                return (
                                    <div
                                        key={variant.id}
                                        className={`${studio.variantRow}${isDisabled ? ` ${studio.variantRowDisabled}` : ""}`}
                                        onClick={() => !isDisabled && toggleVariant(variant.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            className={studio.variantCheckbox}
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => !isDisabled && toggleVariant(variant.id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className={studio.variantLabel}>
                                            <div>{variant.title}</div>
                                            {isDisabled && (
                                                <div className={studio.variantHint}>
                                                    {t("alreadyInProject") || "Already in project"}
                                                    {variant.projectName && `: ${variant.projectName}`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <h2 className={studio.variantsStepTitle}>
                        {t("step2SelectProject") || "Step 2: Select a project"}
                    </h2>

                    <div className={studio.projectSection}>
                        <Dropdown
                            label=""
                            value={selectedProject}
                            options={projectOptions}
                            onChange={(value) => setSelectedProject(value)}
                            placeholder={t("selectProject")}
                            loading={loading}
                            error={!!error}
                            fullWidth
                            isWithoutMargin
                        />
                    </div>

                    {selectedProject === "new" && (
                        <div className={studio.projectSectionNew}>
                            <div className={studio.projectRow}>
                                <TextInput
                                    placeholder={t("nameOfTheNewProject")}
                                    value={newProjectName}
                                    size={"small"}
                                    onChange={(e) => setNewProjectName(e.target.value)}
                                    fullWidth
                                />
                            </div>
                        </div>
                    )}

                    <div className={studio.buttonsCentered}>
                        <Button
                            variant="primary"
                            shape="pill"
                            block
                            label={t("addToProject")}
                            onClick={handleAddToProject}
                            disabled={selectedVariants.size === 0 || !isProjectSelected}
                        />
                        <Button variant="cancel" shape="pill" block label={t("close")} onClick={onClose} />
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default SelectVariantsModal;
